import type { ReactElement } from 'react';
import { FileText, MessageSquare, Share2 } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { formatDate } from '@xdocs/i18n';
import { Button, EmptyState, Select, Skeleton, cn, useI18n } from '@xdocs/ui';
import { useActivityList } from './useActivityList.js';
import { useActivityT } from './useActivityT.js';
import styles from './ActivityPage.module.css';

/** Actions a user can be the actor of in their OWN (non-impersonated)
 * activity, per the current closed `auditAction` enum — see
 * `activity.i18n.ts`'s doc comment for the coverage gap vs. the full F49
 * spec copy. */
const KNOWN_ACTIONS = ['document.create', 'share.publish', 'comment.create'] as const;

/** design/app/26-activity.html's row icon badge — one glyph per known
 * action type, falling back to a generic history mark for anything the
 * closed `auditAction` enum doesn't cover yet. */
const ACTION_ICON: Record<(typeof KNOWN_ACTIONS)[number], LucideIcon> = {
  'document.create': FileText,
  'share.publish': Share2,
  'comment.create': MessageSquare,
};

function describeAction(t: ReturnType<typeof useActivityT>['t'], action: string): string {
  return (KNOWN_ACTIONS as readonly string[]).includes(action) ? t(`activity:action.${action as (typeof KNOWN_ACTIONS)[number]}`) : action;
}

function actionIcon(action: string): LucideIcon {
  return (KNOWN_ACTIONS as readonly string[]).includes(action) ? ACTION_ICON[action as (typeof KNOWN_ACTIONS)[number]] : FileText;
}

const SKELETON_ROW_COUNT = 5;

/** `/activity` (F49): user-scoped `audit_events` read, rendered as a single
 * divided-row feed (design/app/26-activity.html) rather than `<DataTable>`'s
 * columnar table — this resource reads as a narrative history, not tabular
 * data, so it owns a bespoke list local to this route instead of stretching
 * the shared table primitive into a shape it wasn't designed for. */
export function ActivityPage(): ReactElement {
  const { locale } = useI18n();
  const { t } = useActivityT();
  const list = useActivityList();

  const hasActiveFilter = (list.query.q ?? '').length > 0;
  const isEmpty = !list.isLoading && !list.isError && list.rows.length === 0;
  const isNoResults = isEmpty && hasActiveFilter;
  const isFirstUseEmpty = isEmpty && !hasActiveFilter;

  function clearFilter(): void {
    const { q: _q, ...rest } = list.query;
    list.onQueryChange({ ...rest, page: 1 });
  }

  return (
    <div className={cn(styles['page'])}>
      <div className={cn(styles['header'])}>
        <p className={cn(styles['eyebrow'])}>{t('activity:page.eyebrow')}</p>
        <h1 className={cn(styles['title'])}>{t('activity:page.title')}</h1>
      </div>

      <Select
        label={t('activity:filter.label')}
        value={list.query.q ?? ''}
        onChange={(event) => {
          const value = event.target.value;
          const { q: _q, ...rest } = list.query;
          list.onQueryChange(value ? { ...rest, q: value, page: 1 } : { ...rest, page: 1 });
        }}
        options={[
          { value: '', label: t('activity:filter.all') },
          ...KNOWN_ACTIONS.map((action) => ({ value: action, label: describeAction(t, action) })),
        ]}
        containerClassName={cn(styles['filter'])}
      />

      <div className={cn(styles['card'])} aria-busy={list.isLoading || undefined}>
        {list.isError && (
          <EmptyState
            kind="error"
            title={t('activity:list.error.title')}
            action={{ label: t('activity:action.retry'), onClick: list.onRetry }}
          />
        )}

        {!list.isError && list.isLoading && list.rows.length === 0 && (
          <div className={cn(styles['skeletonBlock'])} aria-hidden="true">
            <Skeleton shape="row" count={SKELETON_ROW_COUNT} />
          </div>
        )}

        {!list.isError && isFirstUseEmpty && (
          <EmptyState kind="first-use" title={t('activity:list.empty.title')} description={t('activity:list.empty.description')} />
        )}

        {!list.isError && isNoResults && (
          <EmptyState
            kind="no-results"
            title={t('activity:list.noResults.title', { query: list.query.q ?? '' })}
            action={{ label: t('activity:filter.clear'), onClick: clearFilter }}
          />
        )}

        {!list.isError && !isEmpty && (
          <ul className={cn(styles['rows'])}>
            {list.rows.map((row) => {
              const Icon = actionIcon(row.action);
              return (
                <li key={row.id} className={cn(styles['row'])}>
                  <span className={cn(styles['rowIcon'])} aria-hidden="true">
                    <Icon size={18} />
                  </span>
                  <span className={cn(styles['rowMain'])}>
                    <span className={cn(styles['rowTitle'])}>{describeAction(t, row.action)}</span>
                    <span className={cn(styles['rowSubtitle'])}>
                      {row.targetType && row.targetId ? `${row.targetType} · ${row.targetId}` : t('activity:target.unavailable')}
                    </span>
                  </span>
                  <span className={cn(styles['rowTime'])}>
                    {formatDate(locale, row.createdAt, { dateStyle: 'medium', timeStyle: 'short' })}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {!list.isError && !isEmpty && list.nextCursor && (
        <div className={cn(styles['loadMore'])}>
          {list.loadMoreError ? (
            <p role="alert" className={cn(styles['loadMoreError'])}>
              {list.loadMoreError}{' '}
              <button type="button" className={cn(styles['loadMoreRetry'])} onClick={list.onLoadMore}>
                {t('activity:action.retry')}
              </button>
            </p>
          ) : (
            <Button variant="secondary" state={list.isLoadingMore ? 'loading' : 'idle'} onClick={list.onLoadMore}>
              {t('activity:list.loadMore')}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
